import React from "react"
import { Box, Image } from "@chakra-ui/react"
import {
  BadgeElement,
  BadgeElementType,
  BadgeImageData,
} from "sharedTypes/badge"
import placeholderSvg from "assets/images/placeholder-image.svg?raw"
import { URL_VALIDATOR_REGEX } from "utilities/strings"

type Props = {
  element: BadgeElement
}

// The placeholder may be stretched too, so Stretch to Fit shows on it like on an image
const placeholderImage = `data:image/svg+xml,${encodeURIComponent(
  placeholderSvg.replace("<svg ", "<svg preserveAspectRatio=\"none\" "),
)}`

const BadgeImage = ({ element }: Props) => {
  if (element.type !== BadgeElementType.IMAGE) return null

  const data = element.data as BadgeImageData

  const getCSSFilter = () => {
    if (!data.filters) return undefined

    const { filters } = data
    const filterMap = [
      { value: filters.blur, fn: (v: number) => `blur(${v}px)`, check: (v: number) => v > 0 },
      { value: filters.brightness, fn: (v: number) => `brightness(${v})`, check: (v: number) => v !== 1 },
      { value: filters.contrast, fn: (v: number) => `contrast(${v})`, check: (v: number) => v !== 1 },
      { value: filters.grayscale, fn: (v: number) => `grayscale(${v})`, check: (v: number) => v > 0 },
      { value: filters.hue, fn: (v: number) => `hue-rotate(${v}deg)`, check: (v: number) => v !== 0 },
      { value: filters.invert, fn: (v: number) => `invert(${v})`, check: (v: number) => v > 0 },
      { value: filters.opacity, fn: (v: number) => `opacity(${v})`, check: (v: number) => v !== 1 },
      { value: filters.saturate, fn: (v: number) => `saturate(${v})`, check: (v: number) => v !== 1 },
      { value: filters.sepia, fn: (v: number) => `sepia(${v})`, check: (v: number) => v > 0 },
    ]

    const filterParts = filterMap
      .filter(({ value, check }) => value !== undefined && check(value))
      .map(({ value, fn }) => fn(value!))

    return filterParts.length > 0 ? filterParts.join(" ") : undefined
  }

  const isValidImageUrl = (url: string): boolean => {
    if (!url?.trim()) return false

    return URL_VALIDATOR_REGEX.test(url)
  }

  const hasImage = isValidImageUrl(data.url)
  const imageUrl = hasImage ? data.url : placeholderImage

  return (
    <Box
      w="full"
      h="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
      p={0}
    >
      <Image
        src={imageUrl}
        style={{
          width: "100%",
          height: "100%",
          // The placeholder shows the chosen fit too, before an image is uploaded
          objectFit: data.fit,
          filter: hasImage ? getCSSFilter() : undefined,
        }}
        pointerEvents="none"
      />
    </Box>
  )
}

export default BadgeImage
